import { Box } from "@mui/material";
import { CssBaseline, ThemeProvider } from "@mui/material";

import { useSelector } from "react-redux";
import { Outlet } from "react-router-dom";

import { RootState } from "@store/store";
import { bpmV2Theme } from "@themes/Bpm";
import { useTokenExpirationGuard } from "@hooks/useTokenExpirationGuard";
import { TopbarTitle } from "@components/v2/TopbarTitle";
import { TopbarProfile } from "@components/v2/TopbarProfile";

/**
 * Lightweight shell for the v2 Solicitudes pages. Intentionally does NOT reuse
 * `SolicitudesLayout.tsx` (toolpad `DashboardLayout` + legacy per-company theme)
 * — that layout renders its own sidebar/header, which would double up
 * navigation and clash with `bpmV2Theme`. This layout only wraps `<Outlet/>`
 * with the v2 theme and a minimal top bar, keeping the session-expiry guard
 * behavior identical to the legacy layout.
 */
export const SolicitudesV2Layout = () => {
    const userSession = useSelector((state: RootState) => state.session.user);

    useTokenExpirationGuard();

    const initials =
        `${userSession.name?.charAt(0) ?? ""}${userSession.last_name?.charAt(0) ?? ""}`.toUpperCase() || "??";

    return (
        <ThemeProvider theme={bpmV2Theme}>
            <CssBaseline />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100vh",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexShrink: 0,
                        px: 3,
                        py: 1.5,
                        backgroundColor: "background.paper",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <TopbarTitle ident="BPM" title="Solicitudes" />
                    <TopbarProfile initials={initials} />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                    <Outlet />
                </Box>
            </Box>
        </ThemeProvider>
    );
};
